import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import PublicationActions from "Redux/PublicationRedux";
import useEscapeKey from "../hooks/useEscapeKey";

const PLATFORMS = [
  {
    key: "youtube",
    label: "YouTube",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
    color: "text-red-600",
    bg: "bg-red-50",
    activeBg: "bg-red-100",
    border: "border-red-200",
  },
  {
    key: "instagram",
    label: "Instagram",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
    color: "text-pink-600",
    bg: "bg-pink-50",
    activeBg: "bg-pink-100",
    border: "border-pink-200",
  },
  {
    key: "tiktok",
    label: "TikTok",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      </svg>
    ),
    color: "text-gray-900",
    bg: "bg-gray-50",
    activeBg: "bg-gray-200",
    border: "border-gray-300",
  },
];

const YOUTUBE_CATEGORIES = [
  "Education",
  "Entertainment",
  "Science & Technology",
  "How-to & Style",
  "People & Blogs",
  "Film & Animation",
  "Music",
  "News & Politics",
  "Sports",
  "Travel & Events",
  "Comedy",
  "Gaming",
];

export default function PublishDialog({ isOpen, onClose, videoId, videoUrl, topic, scriptId }) {
  const dispatch = useDispatch();
  useEscapeKey(isOpen, onClose);

  const [activeTab, setActiveTab] = useState("youtube");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Schedule state
  const [scheduleMode, setScheduleMode] = useState("now"); // "now" | "schedule"
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");

  // Platform-specific form data
  const [formData, setFormData] = useState({
    youtube: { title: "", description: "", tags: [], category: "Education", privacy: "public" },
    instagram: { title: "", description: "", hashtags: [] },
    tiktok: { title: "", description: "", hashtags: [] },
  });

  // Tag/hashtag input states
  const [tagInput, setTagInput] = useState("");
  const [hashtagInput, setHashtagInput] = useState("");

  // Load saved metadata when modal opens
  useEffect(() => {
    if (isOpen && scriptId && !hasLoaded) {
      dispatch(
        PublicationActions.publicationLoadMetadataRequest(scriptId, (data, err) => {
          setHasLoaded(true);
          if (err || !data) return;
          const hasData = Object.keys(data).length > 0;
          if (hasData) {
            setFormData((prev) => ({
              youtube: {
                title: data.youtube?.title || prev.youtube.title,
                description: data.youtube?.description || prev.youtube.description,
                tags: data.youtube?.tags || prev.youtube.tags,
                category: data.youtube?.category || prev.youtube.category,
                privacy: data.youtube?.privacy || prev.youtube.privacy,
              },
              instagram: {
                title: data.instagram?.title || prev.instagram.title,
                description: data.instagram?.description || prev.instagram.description,
                hashtags: data.instagram?.hashtags || prev.instagram.hashtags,
              },
              tiktok: {
                title: data.tiktok?.title || prev.tiktok.title,
                description: data.tiktok?.description || prev.tiktok.description,
                hashtags: data.tiktok?.hashtags || prev.tiktok.hashtags,
              },
            }));
          }
        })
      );
    }
  }, [isOpen, scriptId, hasLoaded, dispatch]);

  // Reset hasLoaded when scriptId changes
  useEffect(() => {
    setHasLoaded(false);
  }, [scriptId]);

  const updateField = (platform, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value },
    }));
  };

  const handleMetadataCallback = useCallback((data, err) => {
    setIsGenerating(false);
    if (err) {
      setError(err);
      return;
    }
    if (data) {
      setFormData({
        youtube: {
          title: data.youtube?.title || topic || "",
          description: data.youtube?.description || "",
          tags: data.youtube?.tags || [],
          category: data.youtube?.category || "Education",
          privacy: data.youtube?.privacy || "public",
        },
        instagram: {
          title: data.instagram?.title || topic || "",
          description: data.instagram?.description || "",
          hashtags: data.instagram?.hashtags || [],
        },
        tiktok: {
          title: data.tiktok?.title || topic || "",
          description: data.tiktok?.description || "",
          hashtags: data.tiktok?.hashtags || [],
        },
      });
    }
  }, [topic]);

  const handleGenerateMetadata = useCallback(() => {
    if (!scriptId) {
      setError("No script available to generate metadata.");
      return;
    }
    setIsGenerating(true);
    setError("");
    dispatch(PublicationActions.publicationGenerateMetadataByScriptRequest(scriptId, handleMetadataCallback));
  }, [dispatch, scriptId, handleMetadataCallback]);

  const handleSave = useCallback(() => {
    if (!scriptId) {
      setError("No script available to save metadata.");
      return;
    }
    setIsSaving(true);
    setError("");
    setSuccessMessage("");

    dispatch(
      PublicationActions.publicationSaveMetadataRequest(scriptId, formData, (data, err) => {
        setIsSaving(false);
        if (err) {
          setError(err);
          return;
        }
        setSuccessMessage("Metadata saved successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      })
    );
  }, [dispatch, scriptId, formData]);

  const handleSendEmail = useCallback(() => {
    if (!videoId) return;
    setIsSending(true);
    setError("");
    setSuccessMessage("");

    const platformData = formData[activeTab];
    const scheduledAt =
      scheduleMode === "schedule" && scheduledDate && scheduledTime
        ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString()
        : null;

    const params = {
      platform: activeTab,
      title: platformData.title,
      description: platformData.description,
      tags: platformData.tags || [],
      hashtags: platformData.hashtags || [],
      privacy: platformData.privacy || "public",
      category: platformData.category || null,
      scheduled_at: scheduledAt,
    };

    dispatch(
      PublicationActions.publicationCreateRequest(videoId, params, (pub, createErr) => {
        if (createErr) {
          setIsSending(false);
          setError(createErr);
          return;
        }

        // Now send the email
        dispatch(
          PublicationActions.publicationSendEmailRequest(
            videoId,
            pub.id,
            null, // use default user email
            (result, sendErr) => {
              setIsSending(false);
              if (sendErr) {
                setError(sendErr);
                return;
              }
              setSuccessMessage(result?.message || "Email sent successfully!");
            }
          )
        );
      })
    );
  }, [dispatch, videoId, activeTab, formData, scheduleMode, scheduledDate, scheduledTime]);

  const handleCopyAll = useCallback(() => {
    const data = formData[activeTab];
    let text = "";

    if (data.title) text += `${data.title}\n\n`;
    if (data.description) text += `${data.description}\n\n`;

    if (activeTab === "youtube" && data.tags?.length > 0) {
      text += `Tags: ${data.tags.join(", ")}\n`;
    }
    if ((activeTab === "instagram" || activeTab === "tiktok") && data.hashtags?.length > 0) {
      text += data.hashtags.map((h) => `#${h}`).join(" ") + "\n";
    }

    navigator.clipboard.writeText(text.trim()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [activeTab, formData]);

  const addTag = () => {
    const val = tagInput.trim();
    if (val && !formData.youtube.tags.includes(val)) {
      updateField("youtube", "tags", [...formData.youtube.tags, val]);
    }
    setTagInput("");
  };

  const removeTag = (tag) => {
    updateField(
      "youtube",
      "tags",
      formData.youtube.tags.filter((t) => t !== tag)
    );
  };

  const addHashtag = (platform) => {
    const val = hashtagInput.trim().replace(/^#/, "");
    if (val && !formData[platform].hashtags.includes(val)) {
      updateField(platform, "hashtags", [...formData[platform].hashtags, val]);
    }
    setHashtagInput("");
  };

  const removeHashtag = (platform, hashtag) => {
    updateField(
      platform,
      "hashtags",
      formData[platform].hashtags.filter((h) => h !== hashtag)
    );
  };

  // Reset states when opening
  useEffect(() => {
    if (isOpen) {
      setSuccessMessage("");
      setError("");
      setCopied(false);
      setScheduleMode("now");
      setScheduledDate("");
      setScheduledTime("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentPlatform = PLATFORMS.find((p) => p.key === activeTab);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Publish Video</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Generate metadata & send to your email
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Platform Tabs */}
        <div className="flex gap-2 px-6 py-3 border-b border-gray-100 bg-gray-50/50">
          {PLATFORMS.map((platform) => (
            <button
              key={platform.key}
              onClick={() => {
                setActiveTab(platform.key);
                setSuccessMessage("");
                setError("");
                setHashtagInput("");
                setTagInput("");
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === platform.key
                  ? `${platform.activeBg} ${platform.color} ${platform.border} border shadow-sm`
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {platform.icon}
              {platform.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Generate AI button */}
          <button
            onClick={handleGenerateMetadata}
            disabled={isGenerating}
            className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {isGenerating ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Generating with AI...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                </svg>
                Generate with AI
              </>
            )}
          </button>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Success */}
          {successMessage && (
            <div className="p-4 bg-green-50 border border-green-100 rounded-xl text-sm text-green-700 flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {successMessage}
            </div>
          )}

          {/* Title */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-gray-700">Title</label>
              <span className="text-xs text-gray-400">
                {(formData[activeTab].title || "").length}/100
              </span>
            </div>
            <input
              type="text"
              value={formData[activeTab].title || ""}
              onChange={(e) => updateField(activeTab, "title", e.target.value.slice(0, 100))}
              placeholder={`Enter ${currentPlatform.label} title...`}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-all"
            />
          </div>

          {/* Description / Caption */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-gray-700">
                {activeTab === "youtube" ? "Description" : "Caption"}
              </label>
              <span className="text-xs text-gray-400">
                {(formData[activeTab].description || "").length}/
                {activeTab === "youtube" ? "5000" : "2200"}
              </span>
            </div>
            <textarea
              value={formData[activeTab].description || ""}
              onChange={(e) =>
                updateField(
                  activeTab,
                  "description",
                  e.target.value.slice(0, activeTab === "youtube" ? 5000 : 2200)
                )
              }
              placeholder={
                activeTab === "youtube"
                  ? "Enter video description..."
                  : `Enter ${currentPlatform.label} caption...`
              }
              rows={5}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-all resize-none"
            />
          </div>

          {/* YouTube-specific: Tags */}
          {activeTab === "youtube" && (
            <>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Tags</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    placeholder="Add a tag and press Enter..."
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-base focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
                  />
                  <button
                    onClick={addTag}
                    className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                  >
                    Add
                  </button>
                </div>
                {formData.youtube.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {formData.youtube.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium"
                      >
                        {tag}
                        <button
                          onClick={() => removeTag(tag)}
                          className="hover:text-indigo-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Category</label>
                <select
                  value={formData.youtube.category}
                  onChange={(e) => updateField("youtube", "category", e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none bg-white"
                >
                  {YOUTUBE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Privacy */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Privacy</label>
                <div className="flex gap-3">
                  {["public", "unlisted", "private"].map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium cursor-pointer transition-all ${
                        formData.youtube.privacy === opt
                          ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="privacy"
                        value={opt}
                        checked={formData.youtube.privacy === opt}
                        onChange={() => updateField("youtube", "privacy", opt)}
                        className="hidden"
                      />
                      <span className="capitalize">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Instagram/TikTok-specific: Hashtags */}
          {(activeTab === "instagram" || activeTab === "tiktok") && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700">Hashtags</label>
                <span className="text-xs text-gray-400">
                  {formData[activeTab].hashtags?.length || 0}/
                  {activeTab === "instagram" ? "30" : "10"}
                </span>
              </div>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={hashtagInput}
                  onChange={(e) => setHashtagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addHashtag(activeTab);
                    }
                  }}
                  placeholder="Add a hashtag and press Enter..."
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-base focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
                />
                <button
                  onClick={() => addHashtag(activeTab)}
                  className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                >
                  Add
                </button>
              </div>
              {formData[activeTab].hashtags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {formData[activeTab].hashtags.map((hashtag) => (
                    <span
                      key={hashtag}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${
                        activeTab === "instagram"
                          ? "bg-pink-50 text-pink-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      #{hashtag}
                      <button
                        onClick={() => removeHashtag(activeTab, hashtag)}
                        className="hover:opacity-70"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Schedule Section */}
          <div className="border-t border-gray-100 pt-5">
            <label className="text-sm font-medium text-gray-700 mb-2 block">Email Schedule</label>
            <div className="flex gap-3 mb-3">
              <label
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium cursor-pointer transition-all ${
                  scheduleMode === "now"
                    ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                    : "border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name="schedule"
                  value="now"
                  checked={scheduleMode === "now"}
                  onChange={() => setScheduleMode("now")}
                  className="hidden"
                />
                Send Now
              </label>
              <label
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium cursor-pointer transition-all ${
                  scheduleMode === "schedule"
                    ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                    : "border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name="schedule"
                  value="schedule"
                  checked={scheduleMode === "schedule"}
                  onChange={() => setScheduleMode("schedule")}
                  className="hidden"
                />
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Schedule
              </label>
            </div>

            {scheduleMode === "schedule" && (
              <div className="flex gap-3">
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
                />
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center gap-3">
          <button
            onClick={handleCopyAll}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
          >
            {copied ? (
              <>
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                </svg>
                Copy All
              </>
            )}
          </button>

          <div className="flex-1" />

          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={isSaving || isGenerating}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {isSaving ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Saving...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                </svg>
                Save
              </>
            )}
          </button>

          <button
            onClick={handleSendEmail}
            disabled={isSending || isGenerating || !formData[activeTab].title}
            className={`flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ${
              activeTab === "youtube"
                ? "bg-red-600 hover:bg-red-700"
                : activeTab === "instagram"
                ? "bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                : "bg-gray-900 hover:bg-gray-800"
            }`}
          >
            {isSending ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Sending...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                {scheduleMode === "schedule" ? "Schedule Email" : "Send to Email"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
